import { Button, Callout, Skeleton, toast } from "@/components/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/Dialog";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { useCallback, useState } from "react";
import { useRenewPrescriptions } from "../network/useRenewPrescriptions";
import { useRenewablePrescriptions } from "../network/useRenewablePrescriptions";
import { DrugGroup } from "./DrugGroup";

export const RenewalModal = ({
  open,
  onOpenChange,
  patientId,
  encounterId,
  motherEncounterId,
  eocName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  encounterId: string;
  motherEncounterId: string;
  eocName: string;
}) => {
  const [selectedMedReqIds, setSelectedMedReqIds] = useState<string[]>([]);
  const [selectedDrugCodes, setSelectedDrugCodes] = useState<string[]>([]);

  const addSelectedMedReqId = useCallback(
    (idToAdd: string) => {
      setSelectedMedReqIds([...selectedMedReqIds, idToAdd]);
    },
    [selectedMedReqIds],
  );
  const removeSelectedMedReqId = useCallback(
    (idToRemove: string) => {
      setSelectedMedReqIds(selectedMedReqIds.filter((id) => id !== idToRemove));
    },
    [selectedMedReqIds],
  );
  const addSelectedDrugCode = useCallback(
    (codeToAdd: string) => {
      setSelectedDrugCodes([...selectedDrugCodes, codeToAdd]);
    },
    [selectedDrugCodes],
  );
  const removeSelectedDrugCode = useCallback(
    (codeToRemove: string) => {
      setSelectedDrugCodes(
        selectedDrugCodes.filter((code) => code !== codeToRemove),
      );
    },
    [selectedDrugCodes],
  );

  const {
    data: rxRenewals,
    isFetching,
    isError,
  } = useRenewablePrescriptions({
    patientId,
    motherEncounterId, // Mother Encounter to filter out all current child encounters Rx's
  });

  const {
    mutateAsync: renewPrescriptions,
    isPending: isRenewingPrescriptions,
  } = useRenewPrescriptions({
    patientId,
    encounterId, // Child Encounter to place Rx's on
  });

  const submitRenewals = useCallback(async () => {
    trackEvent(TRACKERS.RENEW_PRESCRIPTIONS);

    try {
      const response = await renewPrescriptions({
        medicationRequestIds: selectedMedReqIds,
        drugIdentifiers: selectedDrugCodes,
      });

      if (response.failedRenewals.length || response.failedNewOrders.length) {
        const failedDrugNames = response.failedRenewals.map(
          (medicationRequest) => medicationRequest.medicationName,
        );
        failedDrugNames.push(...response.failedNewOrders);
        const failedDrugNamesString = failedDrugNames.join(", ");
        toast.error(
          `"The following medications failed to renew. Search for and add them as new prescriptions. ${failedDrugNamesString}"`,
        );
      }
      onOpenChange(false);
    } catch {
      toast.error(
        "Failed to renew prescriptions. Try again or search for and add them as new prescriptions.",
      );
    }
  }, [renewPrescriptions, selectedMedReqIds, selectedDrugCodes, onOpenChange]);

  const closeWithoutRenewing = useCallback(() => {
    trackEvent(TRACKERS.CLOSE_ELIGIBLE_RENEWALS);
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent
          className="w-full max-w-md max-h-[85vh] top-1/2 flex flex-col"
          runOnDialogCloseIconClick={closeWithoutRenewing}
          onInteractOutside={closeWithoutRenewing}
        >
          <DialogHeader>
            <DialogTitle>Rx Renewal: {eocName}</DialogTitle>
            <DialogDescription className="hidden">
              renew rx modal
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto gap-2 flex flex-col flex-1">
            {!isFetching && isError && (
              <Callout
                className="w-full"
                variant="danger"
                title="Failed to load"
                description="Prescriptions eligible for renewal failed to load. Please refresh and try again."
              />
            )}
            {isFetching && (
              <div className="grid gap-1">
                {Array.from({ length: 4 }).map((_, i) => (
                  //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
                  <Skeleton className="h-8" key={i} />
                ))}
              </div>
            )}
            {!isFetching &&
              !isError &&
              rxRenewals?.prescriptionsForDrugs?.map((drugGroup) => (
                <DrugGroup
                  key={drugGroup.drugIdentifier}
                  drugGroup={drugGroup}
                  selectedMedReqIds={selectedMedReqIds}
                  selectedDrugCodes={selectedDrugCodes}
                  addSelectedMedReqId={addSelectedMedReqId}
                  removeSelectedMedReqId={removeSelectedMedReqId}
                  addSelectedDrugCode={addSelectedDrugCode}
                  removeSelectedDrugCode={removeSelectedDrugCode}
                />
              ))}
            {!isFetching &&
              !isError &&
              !rxRenewals?.prescriptionsForDrugs?.length && (
                <Callout
                  className="w-full"
                  variant="info"
                  title="No Prescriptions to Renew"
                  description="There are no prescriptions the patient is still taking that are available to renew."
                />
              )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={closeWithoutRenewing}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={
                !selectedMedReqIds?.length && !selectedDrugCodes?.length
              }
              onClick={submitRenewals}
              isLoading={isRenewingPrescriptions}
            >
              Add Renewals
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
